import { Divider, Form, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"

import { RECEIPT_CONFIGURE_SLUGS } from "../../constants/receipt-configure-slugs"
import SECTION_SCHEMA, {
  SECTION_GROUPS,
  SECTION_SUBTITLES,
  SECTION_TITLES
} from "../../constants/steps"
import { saveSection as saveSectionAction } from "../redux/configure.slice"
import { wizardView } from "../utils/hydrate"
import { visibleQuestions } from "../utils/showIf"
import QuestionField from "./QuestionField/QuestionField"
import ReviewStep from "./ReviewStep/ReviewStep"

import styles from "../shell.module.less"

const { Title, Text } = Typography

const SectionForm = ({
  sectionKey,
  buildingBlocks,
  wizard,
  saveSection,
  showErrors,
  // Whole-step lock: view-only role (§11.15) or the under-review banner
  // (§11.14). Distinct from a question's own `lockAfterFirstSubmit`
  // (Step 3, §11.9), which applies regardless of this flag.
  disabled
}) => {
  const { t } = useTranslation()

  const schema = SECTION_SCHEMA[sectionKey] || []
  const sectionState = buildingBlocks?.[sectionKey] ?? {}
  const visible = visibleQuestions(schema, buildingBlocks)
  const subtitle = SECTION_SUBTITLES[sectionKey]
  // §11.9: Step 3 is editable only during the initial submission.
  const alreadySubmitted = wizardView(wizard).profileFinished

  const handleChange = (patch) => saveSection({ section: sectionKey, patch })

  // Render questions under numbered category headers (SECTION_GROUPS). Only the
  // multi-category steps are grouped; an ungrouped step renders its fields flat
  // (the `leftover` block), so nothing disappears.
  const renderQ = (q) => (
    <QuestionField
      key={q.key}
      question={q}
      sectionState={sectionState}
      onChange={handleChange}
      showErrors={showErrors}
      readOnly={disabled || (q.lockAfterFirstSubmit && alreadySubmitted)}
    />
  )
  const groups = SECTION_GROUPS[sectionKey] || []
  const visibleByKey = new Map(visible.map((q) => [q.key, q]))
  const placed = new Set()
  // An entry in `g.keys` is either a string (single full-width question) or a
  // string[] (a horizontal row of columns, e.g. City + Postal code). Renders
  // the row via a grid wrapper so mobile can collapse it to one column.
  const renderKeyEntry = (entry, idx) => {
    if (Array.isArray(entry)) {
      const rowQs = entry.map((k) => visibleByKey.get(k)).filter(Boolean)
      if (!rowQs.length) return null
      rowQs.forEach((q) => placed.add(q.key))
      return (
        <div className={styles.sectionFormFieldRow} key={`row-${idx}`}>
          {rowQs.map(renderQ)}
        </div>
      )
    }
    const q = visibleByKey.get(entry)
    if (!q) return null
    placed.add(q.key)
    return renderQ(q)
  }
  let groupNum = 0
  const groupNodes = groups
    .map((g, i) => {
      const qs = g.keys
        .flat()
        .map((k) => visibleByKey.get(k))
        .filter(Boolean)
      if (!qs.length) return null
      groupNum += 1
      const num = groupNum
      return (
        <section className={styles.sectionFormGroup} key={g.title}>
          <div className={styles.sectionFormGroupHead}>
            <span className={styles.sectionFormGroupNum}>{num}</span>
            <span className={styles.sectionFormGroupHeading}>
              <span className={styles.sectionFormGroupTitle}>{t(g.title)}</span>
              {g.description ? (
                <span className={styles.sectionFormGroupDesc}>{t(g.description)}</span>
              ) : null}
            </span>
          </div>
          <div className={styles.sectionFormGroupFields}>
            {g.keys.map((entry, idx) => renderKeyEntry(entry, idx))}
            {g.blockHelp ? (
              <span className={styles.sectionFormGroupHelp}>{t(g.blockHelp)}</span>
            ) : null}
          </div>
          {i !== groups?.length - 1 && <Divider />}
        </section>
      )
    })
    .filter(Boolean)
  const leftover = visible.filter((q) => !placed.has(q.key))

  return (
    <Form
      layout="vertical"
      requiredMark={false}
      className={styles.sectionForm}
      data-testid={`deflect-configure-section-${sectionKey}`}
    >
      {/* Title + subtitle grouped tightly (4px) so the subtitle hugs the
          headline; the 16px gap to the fields comes from .sectionForm. */}
      <div className={styles.sectionFormHead}>
        <Title level={4} className={styles.sectionFormTitle}>
          {t(SECTION_TITLES[sectionKey])}
        </Title>
        {subtitle ? <Text type="secondary">{t(subtitle)}</Text> : null}
      </div>

      {sectionKey === RECEIPT_CONFIGURE_SLUGS.preview ? (
        <ReviewStep />
      ) : (
        <div className={styles.sectionFormFields}>
          {groupNodes}
          {leftover.length ? (
            <div className={styles.sectionFormGroupFields}>{leftover.map(renderQ)}</div>
          ) : null}
        </div>
      )}
    </Form>
  )
}

const mapStateToProps = (state) => ({
  buildingBlocks: state.receiptConfigure.profile.buildingBlocks,
  wizard: state.receiptConfigure?.profile?.wizard
})
const mapDispatchToProps = (dispatch) => ({
  saveSection: (payload) => dispatch(saveSectionAction(payload))
})

export default connect(mapStateToProps, mapDispatchToProps)(SectionForm)
